import React from "react";
import ProgressBar from "../components/ProgressBar";
import TopBar from "../components/TopBar";
import Footerr from "../components/Footerr/Footerr";
import {
  RefreshCw,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  Mail,
  Phone,
} from "lucide-react";

function RefundCancellation() {
  return (
    <div className="bg-[#f9f9f7] min-h-screen">
      <ProgressBar />
      <TopBar />

      <div className="py-16 px-4">
        <div className="max-w-5xl mx-auto bg-white border border-[#b99653]/20 shadow-lg rounded-2xl p-10">
          <div className="text-center mb-12">
            <RefreshCw className="w-16 h-16 text-[#b99653] mx-auto mb-4" />
            <h1 className="gold-main inline-block text-4xl font-bold px-6 py-2 rounded-lg text-white shadow-md">
              Refund & Cancellation Policy
            </h1>
            <p className="text-[#101c4c] text-lg mt-4 font-medium">
              Transparent, secure, and customer-first refund process for your
              domain purchases.
            </p>
          </div>

          {/* Policy Overview */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#101c4c] mb-3 flex items-center">
              <CreditCard className="w-6 h-6 mr-2 text-[#b99653]" />
              Policy Overview
            </h2>
            <p className="text-[#101c4c]/90 mb-3">
              At <strong>Brand.Gold Marketplace</strong>, operated by Terces
              Solutions Private Limited, we value transparency and customer
              satisfaction. This policy governs how refunds and cancellations
              are handled for domain purchases made through our platform.
            </p>
            <div className="bg-[#fcdb6a]/20 border-l-4 border-[#b99653] p-4 rounded-lg">
              <p className="text-[#101c4c]/90 font-medium">
                <strong>Important:</strong> All sales are final once domain
                ownership has been transferred to the buyer.
              </p>
            </div>
          </section>

          {/* Refund Eligibility */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#101c4c] mb-4">
              Refund Eligibility
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#fef9eb] border border-[#b99653]/30 rounded-2xl p-5">
                <h3 className="text-lg font-semibold text-[#101c4c] mb-3 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-[#b99653]" />
                  Eligible for Refund
                </h3>
                <ul className="text-[#101c4c]/90 text-sm space-y-2">
                  <li>• Payment processed but ownership transfer failed</li>
                  <li>• Invalid or expired domain listing</li>
                  <li>• Duplicate transaction or charge error</li>
                  <li>• Fraudulent transaction identified by system</li>
                </ul>
              </div>

              <div className="bg-[#fff6f6] border border-red-200 rounded-2xl p-5">
                <h3 className="text-lg font-semibold text-red-700 mb-3 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Not Eligible for Refund
                </h3>
                <ul className="text-[#101c4c]/90 text-sm space-y-2">
                  <li>• Successful domain transfer completion</li>
                  <li>• Change of mind post-purchase</li>
                  <li>• Market price fluctuations</li>
                  <li>• Incorrect order placed by user</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Cancellation Policy */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#101c4c] mb-3">
              Cancellation Policy
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#fcdb6a]/10 border border-[#b99653]/30 rounded-2xl p-5">
                <h3 className="text-lg font-semibold text-[#101c4c] mb-3">
                  Before Payment
                </h3>
                <ul className="text-[#101c4c]/90 text-sm space-y-2">
                  <li>• Orders can be canceled before payment confirmation</li>
                  <li>• No cancellation fees apply</li>
                  <li>• Saved listings can be removed anytime</li>
                </ul>
              </div>
              <div className="bg-[#fff8e6] border border-[#b99653]/30 rounded-2xl p-5">
                <h3 className="text-lg font-semibold text-[#101c4c] mb-3">
                  After Payment
                </h3>
                <ul className="text-[#101c4c]/90 text-sm space-y-2">
                  <li>• Cancellations accepted within 24 hours of purchase</li>
                  <li>• Ownership transfer must not have begun</li>
                  <li>• Subject to a 5% processing fee</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-gold-gradient rounded-2xl p-8 text-white mt-10">
            <h2 className="text-2xl font-bold text-[#101c4c] mb-4">
              Need Assistance?
            </h2>
            <p className="text-[#101c4c]/90 mb-6 font-medium">
              Our support team is here to help with refunds, cancellations, or
              technical issues related to your domain purchases.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-md flex items-center gap-3">
                <Mail className="w-6 h-6 text-[#b99653]" />
                <div>
                  <h4 className="font-semibold text-[#101c4c] text-sm">
                    Email
                  </h4>
                  <p className="text-[#101c4c]/80 text-sm">
                    marketplace@getbrand.gold
                  </p>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-md flex items-center gap-3">
                <Phone className="w-6 h-6 text-[#b99653]" />
                <div>
                  <h4 className="font-semibold text-[#101c4c] text-sm">
                    Phone
                  </h4>
                  <p className="text-[#101c4c]/80 text-sm">+91 95814 30505</p>
                </div>
              </div>
            </div>
          </section>

          {/* Disclaimer */}
          <div className="mt-10 bg-[#fff6f6] border border-red-200 rounded-xl p-6 flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-700 mb-1">
                Important Disclaimer
              </h3>
              <p className="text-[#101c4c]/80 text-sm">
                All purchases on Brand.Gold Marketplace are subject to
                verification and compliance checks. Please review domain details
                carefully before completing your purchase. Refund policies are
                enforced to ensure fairness for all buyers and sellers.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footerr />
    </div>
  );
}

export default RefundCancellation;
